import { useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Loader2, Check } from "lucide-react";
import api from "../../lib/api";
import { track } from "../../lib/analytics";
import { toast } from "sonner";

const REC_LABELS = {
  strong_yes: "Strong Yes",
  yes: "Yes",
  no: "No",
  strong_no: "Strong No",
};

const REC_PILL_CLS = {
  strong_yes: "bg-emerald-600 text-white border-emerald-700",
  yes: "bg-emerald-100 text-emerald-800 border-emerald-200",
  no: "bg-amber-100 text-amber-800 border-amber-200",
  strong_no: "bg-red-100 text-red-800 border-red-200",
};

/**
 * Reusable recommendation pill — used in 3 places:
 *  1. Scorecards section on InterviewDetail
 *  2. Candidate timeline aggregated row
 *  3. Role pipeline candidate row
 *
 * Pre-aggregation is deliberately NOT done. Each pill = one submitted scorecard.
 * Draft scorecards render as outlined pills (per locked spec).
 */
export function RecommendationPill({ recommendation, status, fillerName, tooltipExtra }) {
  const cls = REC_PILL_CLS[recommendation] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = REC_LABELS[recommendation] || "Draft";
  const isDraft = status === "draft";
  const tooltip = [
    fillerName ? `${fillerName}` : null,
    isDraft ? "Draft (not submitted)" : null,
    tooltipExtra,
  ].filter(Boolean).join(" · ");
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${
        isDraft ? "border-slate-300 bg-white text-slate-500 italic" : cls
      }`}
      title={tooltip}
      data-testid={`recommendation-pill-${recommendation || "draft"}`}
    >
      {isDraft ? `Draft — ${fillerName || "in progress"}` : label}
    </span>
  );
}


/**
 * Right-side drawer (560px) for filling out a scorecard. Auto-saves on
 * blur + 5s heartbeat. Submit is gated until all ratings + overall set.
 * Per locked spec: drawer (not modal) so transcript stays visible left.
 */
export function ScorecardDrawer({ open, onClose, scorecardId, onUpdated }) {
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveError, setSaveError] = useState("");
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef(null);
  // Latest-snapshot ref so the 5s heartbeat (and any other indirect saver)
  // always reads the freshest scorecard, never a stale closure value. The
  // primary fix below is to pass the explicit next-state into flushSave()
  // from every user action — this ref is belt-and-suspenders.
  const scorecardRef = useRef(null);
  useEffect(() => { scorecardRef.current = scorecard; }, [scorecard]);

  // Load on open
  useEffect(() => {
    if (!open || !scorecardId) return;
    let cancel = false;
    setLoading(true);
    setSaveError("");
    api.get(`/scorecards/${scorecardId}`)
      .then(({ data }) => { if (!cancel) setScorecard(data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load scorecard"))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, scorecardId]);

  // Auto-save heartbeat (5s). Reads via scorecardRef so it can't miss a
  // late-arriving state update.
  useEffect(() => {
    if (!open) return;
    saveTimerRef.current = setInterval(() => {
      if (dirtyRef.current && scorecardRef.current) flushSave(scorecardRef.current);
    }, 5000);
    return () => clearInterval(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Send a PATCH with the EXPLICIT snapshot passed in by the caller — never
   * read from the closure `scorecard` state. This is the fix for the
   * production bug where clicking a recommendation pill optimistically set
   * local state, then setTimeout-scheduled handleBlurSave read the OLD
   * closure value, PATCH'd null, and the server response clobbered the
   * just-set pill — visually reverting the selection.
   */
  const flushSave = async (snapshot) => {
    if (!snapshot || snapshot.status === "submitted") return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/scorecards/${snapshot.scorecard_id}`, {
        ratings: snapshot.ratings.map(r => ({
          competency_id: r.competency_id,
          rating: r.rating, notes: r.notes || "",
        })),
        overall_recommendation: snapshot.overall_recommendation,
        overall_notes: snapshot.overall_notes || "",
      });
      setScorecard(data);
      setLastSavedAt(new Date());
      setSaveError("");
      dirtyRef.current = false;
      if (onUpdated) onUpdated(data);
    } catch (e) {
      // Visible-not-silent: surface the failure in the drawer header so the
      // user knows their change isn't safe. Also keep local state intact —
      // do NOT setScorecard from server (would revert their pending edit).
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.error ? String(detail.error)
        : e?.message || "Save failed";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (patch) => {
    const next = scorecard ? { ...scorecard, ...patch } : scorecard;
    if (next) {
      setScorecard(next);
      dirtyRef.current = true;
    }
    return next;
  };

  const updateRating = (competencyId, partial) => {
    if (!scorecard) return null;
    const next = {
      ...scorecard,
      ratings: scorecard.ratings.map(r =>
        r.competency_id === competencyId ? { ...r, ...partial } : r
      ),
    };
    setScorecard(next);
    dirtyRef.current = true;
    return next;
  };

  // onBlur handler for free-text fields — uses the ref so it always reads
  // the freshest snapshot regardless of when the blur event fires.
  const handleBlurSave = () => {
    if (dirtyRef.current && scorecardRef.current) flushSave(scorecardRef.current);
  };

  // Validation: every competency rated AND recommendation set
  const incompleteReason = useMemo(() => {
    if (!scorecard) return "Loading…";
    const missing = scorecard.ratings.filter(r => r.rating === null || r.rating === undefined);
    if (missing.length > 0) return `Rate all ${scorecard.ratings.length} competencies (${missing.length} remaining)`;
    if (!scorecard.overall_recommendation) return "Choose an overall recommendation";
    return null;
  }, [scorecard]);

  const handleSubmit = async () => {
    if (!scorecard || incompleteReason) return;
    if (dirtyRef.current) await flushSave(scorecard);
    try {
      const { data } = await api.post(`/scorecards/${scorecard.scorecard_id}/submit`);
      setScorecard(data);
      track("scorecard_submitted", {});
      if (onUpdated) onUpdated(data);
      toast.success("Scorecard submitted");
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.missing_ratings ? "Some competencies still need ratings"
        : detail?.missing_overall_recommendation ? "Choose an overall recommendation"
        : "Submit failed";
      toast.error(msg);
    }
  };

  const readOnly = scorecard?.status === "submitted";

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { handleBlurSave(); onClose(); } }}>
      <SheetContent side="right" className="w-[560px] sm:max-w-[560px] flex flex-col p-0" data-testid="scorecard-drawer">
        <SheetHeader className="border-b border-ink-200 px-6 py-4 bg-white">
          <SheetTitle className="font-display text-lg font-medium">Scorecard</SheetTitle>
          <SheetDescription className="text-[12px] text-ink-500">
            {scorecard ? (
              <>
                {scorecard.template_snapshot?.name} ·{" "}
                <span className={`font-medium ${readOnly ? "text-emerald-700" : "text-slate-600"}`}>
                  {readOnly ? "Submitted" : "Draft"}
                </span>
                {!readOnly && saving && " · saving…"}
                {!readOnly && !saving && !saveError && lastSavedAt && ` · saved ${Math.max(1, Math.round((Date.now() - lastSavedAt.getTime()) / 1000))}s ago`}
              </>
            ) : (loading ? "Loading…" : "")}
          </SheetDescription>
          {/* Visible save-error banner — replaces the previous silent
              toast-and-revert pattern that hid the production bug. */}
          {saveError && !readOnly && (
            <div
              className="mt-2 flex items-start justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800"
              data-testid="scorecard-save-error"
            >
              <span>Couldn't save: {saveError}</span>
              <button
                type="button"
                onClick={() => scorecardRef.current && flushSave(scorecardRef.current)}
                className="font-medium underline hover:no-underline whitespace-nowrap"
                data-testid="scorecard-save-error-retry"
              >
                Retry
              </button>
            </div>
          )}
        </SheetHeader>

        {scorecard ? (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 bg-white">
            {scorecard.template_snapshot.competencies.map((c, idx) => {
              const rating = scorecard.ratings.find(r => r.competency_id === c.competency_id) || { rating: null, notes: "" };
              return (
                <div key={c.competency_id} className="space-y-2" data-testid={`scorecard-competency-${idx}`}>
                  <div>
                    <div className="text-[14px] font-semibold text-ink-900">{c.name}</div>
                    {c.description && (
                      <div className="text-[12px] text-ink-500 mt-0.5">{c.description}</div>
                    )}
                  </div>
                  {/* 1-5 selector */}
                  <div className="flex items-center gap-2 pt-1" role="radiogroup" aria-label={`Rating for ${c.name}`}>
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={rating.rating === n}
                        disabled={readOnly}
                        data-testid={`scorecard-rating-${idx}-${n}`}
                        onClick={() => {
                          // Bug-fix pattern: build the explicit next-snapshot,
                          // then flushSave with that snapshot. Never via
                          // closure. See flushSave docstring for why.
                          const next = updateRating(c.competency_id, { rating: n });
                          if (next) flushSave(next);
                        }}
                        className={`h-9 w-9 rounded-full border text-[13px] font-medium transition ${
                          rating.rating === n
                            ? "bg-emerald-600 border-emerald-700 text-white"
                            : "bg-white border-ink-300 text-ink-600 hover:border-emerald-600 hover:text-emerald-700"
                        } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <Textarea
                    placeholder="Notes (optional)"
                    value={rating.notes || ""}
                    disabled={readOnly}
                    maxLength={2000}
                    rows={2}
                    onChange={(e) => updateRating(c.competency_id, { notes: e.target.value })}
                    onBlur={handleBlurSave}
                    className="text-[13px] resize-y"
                    data-testid={`scorecard-notes-${idx}`}
                  />
                </div>
              );
            })}

            <div className="border-t border-ink-200 pt-5 space-y-3">
              <Label className="text-[13px] font-semibold text-ink-900">Overall recommendation</Label>
              <div className="flex flex-wrap gap-2">
                {["strong_yes", "yes", "no", "strong_no"].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    disabled={readOnly}
                    data-testid={`scorecard-recommendation-${opt}`}
                    onClick={() => {
                      // Build the explicit next-snapshot and pass it directly
                      // to flushSave. This is the production bug fix:
                      // previously this read the stale `scorecard` from
                      // closure via handleBlurSave, PATCH'd null, and the
                      // server response clobbered the just-set local state.
                      const next = updateField({ overall_recommendation: opt });
                      if (next) flushSave(next);
                    }}
                    className={`inline-flex items-center rounded-md border px-3 py-1.5 text-[12px] font-medium transition ${
                      scorecard.overall_recommendation === opt
                        ? REC_PILL_CLS[opt]
                        : "border-ink-300 bg-white text-ink-600 hover:border-ink-400"
                    } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {REC_LABELS[opt]}
                  </button>
                ))}
              </div>
              <Textarea
                placeholder="Summary (optional)"
                value={scorecard.overall_notes || ""}
                disabled={readOnly}
                maxLength={4000}
                rows={3}
                onChange={(e) => updateField({ overall_notes: e.target.value })}
                onBlur={handleBlurSave}
                className="text-[13px] resize-y"
                data-testid="scorecard-overall-notes"
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white">
            <Loader2 className="h-5 w-5 animate-spin text-ink-400" />
          </div>
        )}

        <div className="border-t border-ink-200 px-6 py-3 bg-ink-50 flex items-center justify-between gap-3">
          <div className="text-[11px] text-ink-500" title={incompleteReason || "Ready to submit"}>
            {incompleteReason ? incompleteReason : <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3 w-3" /> Ready to submit</span>}
          </div>
          {!readOnly && (
            <Button
              onClick={handleSubmit}
              disabled={!!incompleteReason || saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="scorecard-submit-button"
            >
              Submit scorecard
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}


/**
 * Section rendered on InterviewDetail below the transcript summary.
 * Lists all submitted/draft scorecards as inline pill rows, with a
 * "+ Add scorecard" CTA when the current user is HM+ and has no scorecard yet.
 */
export function ScorecardsSection({ interviewId, currentUser, currentMemberRole }) {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const canCreate = ["admin", "recruiter", "hiring_manager"].includes(currentMemberRole);

  const load = async () => {
    try {
      const { data } = await api.get(`/interviews/${interviewId}/scorecards`);
      setScorecards(data);
    } catch (e) {
      // 404 means interview not found — quiet fail; outer page handles
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [interviewId]);

  const myScorecard = scorecards.find(s => s.filled_by === currentUser?.user_id);

  const handleAdd = async () => {
    if (myScorecard) { setActiveId(myScorecard.scorecard_id); setDrawerOpen(true); return; }
    try {
      const { data } = await api.post(`/interviews/${interviewId}/scorecards`);
      setScorecards(s => [...s, data]);
      setActiveId(data.scorecard_id);
      setDrawerOpen(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "scorecard_already_exists") {
        setActiveId(detail.existing_scorecard_id);
        setDrawerOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't create scorecard");
      }
    }
  };

  return (
    <div className="hp-card p-4" data-testid="scorecards-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[15px] font-medium text-ink-900">
          Scorecards{scorecards.length > 0 && ` (${scorecards.length})`}
        </h3>
        {canCreate && (
          <Button
            onClick={handleAdd}
            size="sm"
            variant="outline"
            className="text-[12px] border-emerald-700 text-emerald-800 hover:bg-emerald-50"
            data-testid="add-scorecard-button"
          >
            {myScorecard ? (myScorecard.status === "draft" ? "Finish my scorecard" : "View my scorecard") : "+ Add scorecard"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-[12px] text-ink-500">Loading…</div>
      ) : scorecards.length === 0 ? (
        <div className="text-[13px] text-ink-500 py-1">
          {canCreate
            ? "No scorecards yet. Add yours to record an objective recommendation."
            : "No scorecards have been submitted for this interview yet."}
        </div>
      ) : (
        <ul className="space-y-2">
          {scorecards.map(sc => {
            const isMine = sc.filled_by === currentUser?.user_id;
            return (
              <li
                key={sc.scorecard_id}
                className="flex items-center justify-between gap-3 rounded-md border border-ink-100 px-3 py-2 hover:bg-ink-50 cursor-pointer"
                onClick={() => { setActiveId(sc.scorecard_id); setDrawerOpen(true); }}
                data-testid={`scorecard-row-${sc.scorecard_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[13px] text-ink-900 truncate">
                    {sc.filled_by_name || sc.filled_by_email}
                    {isMine && <span className="ml-1 text-ink-400 text-[12px]">(you)</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <RecommendationPill
                    recommendation={sc.overall_recommendation}
                    status={sc.status}
                    fillerName={sc.filled_by_name}
                  />
                  <span className="text-[11px] text-ink-400 font-mono tabular-nums w-[60px] text-right">
                    {sc.submitted_at
                      ? new Date(sc.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                      : "—"}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ScorecardDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); load(); }}
        scorecardId={activeId}
        onUpdated={(updated) => {
          setScorecards(s => s.map(x => x.scorecard_id === updated.scorecard_id ? updated : x));
        }}
      />
    </div>
  );
}
